import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { getEvent, getRsvps, getItems, setStatus } from "@/lib/api";
import { PasscodeGate } from "@/components/admin/PasscodeGate";
import { RsvpTable } from "@/components/admin/RsvpTable";
import { ItemManager } from "@/components/admin/ItemManager";
import { ItemsView } from "@/components/admin/ItemsView";
import { Switch } from "@/components/ui/switch";
import { LogOut, RefreshCw, PowerOff, Power } from "lucide-react";

export default function AdminPage() {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("orsers_admin") === "1");
  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, rs, it] = await Promise.all([getEvent(), getRsvps(), getItems()]);
      setEvent(ev); setRsvps(rs); setItems(it);
    } catch {
      toast.error("Failed to load admin data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) refresh();
  }, [unlocked, refresh]);

  const handleToggle = async (open) => {
    try {
      const res = await setStatus(open ? "open" : "closed");
      setEvent((e) => ({ ...e, rsvp_status: res.rsvp_status, override: res.override }));
      toast.success(open ? "RSVPs are now OPEN." : "RSVPs are now CLOSED.");
    } catch {
      toast.error("Could not update status.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("orsers_admin");
    setUnlocked(false);
  };

  if (!unlocked) return <PasscodeGate onUnlock={() => setUnlocked(true)} />;

  const isOpen = event?.rsvp_status === "open";

  return (
    <div className="relative z-[2] min-h-screen bg-retro-cream" data-testid="admin-page">
      <header className="border-b-2 border-retro-ink bg-retro-ink px-6 py-5 sm:px-10">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <h1 className="font-chango text-2xl text-retro-yellow sm:text-3xl">Orsers HQ</h1>
          <div className="flex items-center gap-2">
            <button onClick={refresh} data-testid="refresh-button" className="inline-flex items-center gap-1 rounded-full border-2 border-white/40 bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/20">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} data-testid="logout-button" className="inline-flex items-center gap-1 rounded-full border-2 border-white/40 bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/20">
              <LogOut className="h-4 w-4" /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-6 py-8 sm:px-10">
        {/* Status toggle */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-2 border-retro-ink bg-white p-5 shadow-retro-lg" data-testid="status-toggle-card">
          <div className="flex items-center gap-3">
            {isOpen ? <Power className="h-7 w-7 text-retro-blue" /> : <PowerOff className="h-7 w-7 text-retro-orange" />}
            <div>
              <p className="font-chango text-xl text-retro-ink">
                RSVPs are {isOpen ? "OPEN" : "CLOSED"}
              </p>
              <p className="text-sm font-medium text-retro-ink/70">
                {event?.override ? "Manually overridden." : "Following the Aug 10 deadline."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-chango text-sm text-retro-ink/70">{isOpen ? "Open" : "Closed"}</span>
            <Switch checked={isOpen} onCheckedChange={handleToggle} data-testid="rsvp-status-switch" />
          </div>
        </div>

        <RsvpTable rsvps={rsvps} onChange={refresh} />
        <ItemsView items={items} rsvps={rsvps} />
        <ItemManager items={items} onChange={refresh} />
      </main>
    </div>
  );
}
